import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@/contexts';
import { Dialog, DialogContent, DialogTitle } from '@/components/Dialog/Dialog';
import { onFunctionSearchOpen } from './events';
import type { FunctionSearchSource } from './types';

const loadPanel = () =>
  import('./FunctionSearchPanel').then((module) => ({ default: module.FunctionSearchPanel }));
const FunctionSearchPanel = lazy(loadPanel);

/**
 * Cáscara para el primer ⌘K en red lenta: si el preload en idle no llegó, el atajo
 * responde igual con el marco del panel mientras el chunk baja. Es el MISMO `Dialog` +
 * `DialogContent` que arma `FunctionSearchPanel` (cortina, z, radio, sombra y posición
 * copiados de ahí): al llegar el chunk el esqueleto se reemplaza sin cambiar de forma.
 * `Dialog` ya viaja en el arranque (lo usa el shell), así que no suma peso.
 * Si cambia la className del `DialogContent` del panel, cambiarla también acá.
 */
const PANEL_OVERLAY = '!bg-frost-overlay/45';
const PANEL_SHELL = [
  '!h-dvh !overflow-hidden !p-0',
  'motion-safe:max-sm:data-[state=open]:slide-in-from-bottom-4',
  'sm:!top-[62px] sm:!h-auto sm:!max-h-none sm:!max-w-[640px] sm:!translate-y-0',
  '!border-0 sm:!rounded-2xl sm:!shadow-[0_2px_6px_rgba(var(--f-ink-rgb),0.06),0_28px_64px_-24px_rgba(var(--f-ink-rgb),0.42)]',
  'sm:motion-safe:data-[state=open]:zoom-in-95 sm:motion-safe:data-[state=closed]:zoom-out-95'
].join(' ');

const PanelSkeleton = ({ onClose }: { onClose: () => void }) => (
  <Dialog
    open
    onOpenChange={(next) => {
      if (!next) onClose();
    }}
  >
    <DialogContent overlayClassName={PANEL_OVERLAY} className={PANEL_SHELL} aria-describedby={undefined}>
      <DialogTitle className="sr-only">Buscar funciones de Ágora</DialogTitle>
      <div className="flex min-h-16 items-center gap-3 border-b border-[rgba(var(--f-ink-rgb),0.08)] px-[18px] sm:min-h-[60px] sm:pl-[22px] sm:pr-5">
        <span
          aria-hidden
          className="h-4 w-4 shrink-0 rounded-full border-2 border-frost-hair-stronger border-t-frost-brand motion-safe:animate-spin"
        />
        <span className="text-fs-16 text-frost-ink-mid">Cargando el buscador…</span>
      </div>
      <div aria-hidden className="px-4 py-4 sm:px-5">
        <div className="mb-3 h-4 w-44 rounded-frost-sm bg-frost-hair motion-safe:animate-pulse" />
        <div className="mb-3 h-4 w-64 rounded-frost-sm bg-frost-hair motion-safe:animate-pulse" />
        <div className="h-4 w-52 rounded-frost-sm bg-frost-hair motion-safe:animate-pulse" />
      </div>
    </DialogContent>
  </Dialog>
);

type Props = { enabled?: boolean };

/**
 * Cáscara que vive en el Layout de todas las pantallas. Sólo escucha el atajo y el
 * evento de apertura: el panel, el catálogo y cmdk se cargan aparte, así el buscador
 * no le suma peso al primer render de la app. Una vez abierto queda montado, para
 * que la segunda apertura sea instantánea y la animación de cierre no se corte.
 */
export const FunctionSearchDialog = ({ enabled = true }: Props) => {
  const { session } = useAuth();
  const [open, setOpen] = useState(false);
  const [openMeta, setOpenMeta] = useState<{
    id: number;
    source: FunctionSearchSource;
    openedAt: number;
  } | null>(null);
  const openRef = useRef(false);
  // Solo el dueño: el staff no tiene buscador (ni atajo, ni triggers — ver FunctionSearchTrigger).
  const canOpen = enabled && !!session?.vendor && !session.is_staff;

  useEffect(() => {
    openRef.current = open;
  }, [open]);

  const openSearch = useCallback(
    (source: FunctionSearchSource) => {
      if (!canOpen || openRef.current) return;
      setOpenMeta((previous) => ({ id: (previous?.id ?? 0) + 1, source, openedAt: Date.now() }));
      setOpen(true);
    },
    [canOpen]
  );

  useEffect(() => onFunctionSearchOpen(openSearch), [openSearch]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        openSearch('keyboard');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openSearch]);

  // El catálogo pesa bastante más que el atajo que lo abre: se precarga cuando el
  // navegador queda ocioso, así el primer Ctrl+K encuentra el chunk ya descargado.
  useEffect(() => {
    if (!canOpen || openMeta) return;
    if (typeof window.requestIdleCallback === 'function') {
      const handle = window.requestIdleCallback(() => void loadPanel());
      return () => window.cancelIdleCallback(handle);
    }
    const timer = window.setTimeout(() => void loadPanel(), 2_000);
    return () => window.clearTimeout(timer);
  }, [canOpen, openMeta]);

  if (!openMeta) return null;

  return (
    <Suspense fallback={open ? <PanelSkeleton onClose={() => setOpen(false)} /> : null}>
      <FunctionSearchPanel
        key={openMeta.id}
        open={open}
        onOpenChange={setOpen}
        source={openMeta.source}
        openedAt={openMeta.openedAt}
      />
    </Suspense>
  );
};
